import React from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Images,
  Newspaper,
  CalendarDays,
  Building2,
  Briefcase,
  FileText,
  Settings,
  LogOut,
  ExternalLink,
  KeyRound
} from 'lucide-react';
import { useAuth } from '../../lib/AuthContext';

const items = [
  { to: '/admin', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/admin/slides', label: 'Hero Slides', icon: Images },
  { to: '/admin/news', label: 'News', icon: Newspaper },
  { to: '/admin/events', label: 'Events', icon: CalendarDays },
  { to: '/admin/ministries', label: 'Ministries', icon: Building2 },
  { to: '/admin/investment', label: 'Investment', icon: Briefcase },
  { to: '/admin/documents', label: 'Documents', icon: FileText },
  { to: '/admin/settings', label: 'Site Settings', icon: Settings },
  { to: '/admin/password', label: 'Password', icon: KeyRound }
];

const AdminLayout = () => {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (!loading && !user) navigate('/admin/login', { replace: true });
  }, [loading, user, navigate]);

  if (loading || !user) {
    return (
      <div className="flex h-screen items-center justify-center bg-[#FAF6EC] text-[#0B2545]">
        Loading...
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-[#FAF6EC]">
      {/* Sidebar */}
      <aside className="sticky top-0 hidden h-screen w-64 flex-col border-r border-[#E6DDC8] bg-[#0B2545] text-white md:flex">
        <div className="flex items-center gap-3 border-b border-white/10 px-5 py-5">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-[#C9A86A] text-white font-serif text-lg">
            S
          </div>
          <div className="leading-tight">
            <div className="font-serif text-[15px] font-bold">State House</div>
            <div className="text-[10px] tracking-widest text-[#C9A86A]">ADMIN PORTAL</div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto px-3 py-4">
          <ul className="space-y-1">
            {items.map((it) => (
              <li key={it.to}>
                <NavLink
                  to={it.to}
                  end={it.end}
                  className={({ isActive }) =>
                    `flex items-center gap-3 rounded-md px-3 py-2.5 text-[13.5px] font-medium transition ${
                      isActive
                        ? 'bg-[#C9A86A] text-white'
                        : 'text-white/75 hover:bg-white/5 hover:text-white'
                    }`
                  }
                >
                  <it.icon size={16} />
                  {it.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
        <div className="border-t border-white/10 p-3">
          <Link
            to="/"
            target="_blank"
            className="mb-2 flex w-full items-center justify-center gap-2 rounded-md border border-white/20 px-3 py-2 text-[12.5px] font-medium text-white/80 hover:bg-white/5"
          >
            <ExternalLink size={13} /> View Site
          </Link>
          <button
            onClick={logout}
            className="flex w-full items-center justify-center gap-2 rounded-md bg-white/5 px-3 py-2 text-[12.5px] font-medium text-white hover:bg-white/10"
          >
            <LogOut size={13} /> Sign Out
          </button>
          <div className="mt-3 text-center text-[11px] text-white/40">
            Signed in as <span className="text-[#C9A86A]">{user.username}</span>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1">
        <header className="sticky top-0 z-30 flex items-center justify-between border-b border-[#E6DDC8] bg-white px-6 py-3 md:hidden">
          <div className="font-serif text-[15px] font-bold text-[#0B2545]">
            State House Admin
          </div>
          <button
            onClick={logout}
            className="text-[12px] text-[#0B2545]/70 underline"
          >
            Sign out
          </button>
        </header>
        <main className="p-6 md:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
